'use client'
import { useBreakpoint, useOpenSignUp } from '@l2p/apps-shared'
import {
  useAddFavoriteGame,
  useFavoriteGames,
  useIsAuthorized,
  useRemoveFavoriteGame,
} from '@l2p/swr-hooks'
import cn, { Argument as CnArgument } from 'classnames'
import { useTranslations } from 'next-intl'
import Image from 'next/image'
import React, { SetStateAction, useCallback, useMemo } from 'react'
import { LoadingSpinner } from '../Loaders'
import styles from './styles.module.scss'

interface FavoriteProps {
  gameId?: string | number
  withText?: boolean
  setRemoveAnimation?: React.Dispatch<SetStateAction<boolean>> | null
  withBackground?: boolean
  withAnimation?: boolean
  image: string
  imageFill: string
  classNames?: CnArgument
}

const AddFavoriteIcon: React.FC<FavoriteProps> = ({
  gameId,
  withBackground = true,
  withText = false,
  image,
  imageFill,
  classNames,
}) => {
  const breakpoints = useBreakpoint()
  const { addFavoriteGame, isSubmitting: isAdding } = useAddFavoriteGame()
  const { removeFavoriteGame, isSubmitting: isRemoving } =
    useRemoveFavoriteGame()
  const { data, isValidating } = useFavoriteGames()

  const isFavorite = useMemo(() => {
    return !!(gameId && data?.byId?.[gameId])
  }, [gameId, data?.byId])
  const t = useTranslations('translation')

  const isAuth = useIsAuthorized()
  const signUpNavigate = useOpenSignUp()

  const addToFavorites = useCallback(() => {
    if (gameId) addFavoriteGame({ gameId: String(gameId) })
  }, [addFavoriteGame, gameId])

  const removeFromFavorites = useCallback(() => {
    if (gameId) removeFavoriteGame({ gameId: String(gameId) })
  }, [removeFavoriteGame, gameId])

  const onChangeFavorite = useCallback(() => {
    isFavorite ? removeFromFavorites() : addToFavorites()
  }, [isFavorite, removeFromFavorites, addToFavorites])

  const onClickHandler = useCallback(() => {
    isAuth ? onChangeFavorite() : signUpNavigate()
  }, [isAuth, onChangeFavorite, signUpNavigate])

  return (
    <button
      className={cn(
        styles.button,
        {
          [styles.background]: withBackground,
          [styles.withText]: withText,
        },
        classNames,
      )}
      onClick={onClickHandler}
      style={{ opacity: isAdding || isRemoving ? '0.5' : '1' }}
    >
      {isValidating || isAdding || isRemoving ? (
        <div>
          <LoadingSpinner size="sm" />
        </div>
      ) : (
        <>
          <Image
            width={breakpoints && breakpoints.mobile ? 18 : 22}
            height={breakpoints && breakpoints.mobile ? 18 : 22}
            src={isFavorite ? imageFill : image}
            alt={isFavorite ? t('removeFromFavorites') : t('addToFavorites')}
          />
          {withText && (
            <div className={styles.text}>
              {isFavorite ? t('removeFromFavorites') : t('addToFavorites')}
            </div>
          )}
        </>
      )}
    </button>
  )
}

export default AddFavoriteIcon
